/**
 * Per-user AppSync notification bus — subscribes to
 * ``/default/user/{userSub}``.
 *
 * Why a dedicated hook (vs reusing ``useAgentChannel``)
 * -----------------------------------------------------
 * The agent channel is scoped to a ``(networkId, poolId)`` room and
 * surfaces a list of messages the caller diffs locally. The user
 * channel is a *global* subscription — mounted once per app session,
 * typically in the outer shell — and its purpose is "invalidate a
 * specific slice of client state". Exposing messages to the caller
 * would invite cache drift (stale events replayed on re-mount), so we
 * instead fire ``onNotification`` exactly once per event and keep zero
 * local state. That also keeps this file insignificant at bundle size.
 *
 * Transport is the shared ``services/appsyncEvents`` singleton, so this
 * subscription is multiplexed onto the same WebSocket used by the agent
 * and workbench hooks — no extra handshake per hook.
 *
 * Server-side
 * -----------
 * See ``stepflow_core.events.publish_user_notification`` +
 * ``appsync_events_authorizer._parse_channel`` (user-channel branch).
 * The authorizer enforces ``channel.userSub == caller.sub`` so one
 * user's bus is private even from workspace OWNER / ADMIN.
 */
import { useEffect, useRef, useState } from 'react';
import {
  subscribeAppSyncEvents,
  appsyncEventEndpoints,
  type AppSyncEventsUnsubscribe,
} from '../services/appsyncEvents';

export type UserNotification = {
  eventType: string;
  eventId?: string;
  timestamp?: string;
  actorSub?: string;
  payload?: Record<string, unknown>;
  // Convenience passthroughs lifted out of ``payload`` when present, so
  // callers can switch without drilling. ``reason`` is the primary
  // discriminator — see ``USER_NOTIFICATION_REASONS`` in events.py.
  reason?: string;
};

function parseUserEventEntry(e: unknown): Record<string, unknown> | null {
  if (typeof e === 'string') {
    try {
      const parsed = JSON.parse(e);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }
  if (e && typeof e === 'object' && !Array.isArray(e)) {
    return e as Record<string, unknown>;
  }
  return null;
}

/**
 * Subscribe to ``/default/user/{userSub}`` and fire ``onNotification``
 * on each event. Pass ``undefined`` for ``userSub`` to disable (e.g.
 * during logout) — the subscription is torn down cleanly.
 *
 * Returns ``{ connected, error }`` for diagnostics; most callers can
 * ignore them. ``onNotification`` is captured via a ref so callers
 * don't have to ``useCallback`` it — passing a fresh closure each
 * render is fine and won't retrigger the subscription.
 */
export function useUserChannel(
  userSub: string | undefined,
  onNotification: (notif: UserNotification) => void,
) {
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onNotificationRef = useRef(onNotification);
  useEffect(() => {
    onNotificationRef.current = onNotification;
  }, [onNotification]);

  const { httpDns, realtimeDns } = appsyncEventEndpoints();

  useEffect(() => {
    const sub = (userSub || '').trim();
    if (!sub || !httpDns || !realtimeDns) {
      setConnected(false);
      return undefined;
    }

    setError(null);
    let active = true;
    const channelPath = `/default/user/${sub}`;

    const unsubscribe: AppSyncEventsUnsubscribe = subscribeAppSyncEvents(
      httpDns,
      realtimeDns,
      channelPath,
      {
        onData: (list) => {
          if (!active) return;
          for (const entry of list) {
            const env = parseUserEventEntry(entry);
            if (!env) continue;
            const payload =
              env.payload && typeof env.payload === 'object'
                ? (env.payload as Record<string, unknown>)
                : {};
            const reason =
              typeof payload.reason === 'string'
                ? (payload.reason as string)
                : undefined;
            try {
              onNotificationRef.current({
                eventType: String(env.eventType || ''),
                eventId:
                  typeof env.eventId === 'string'
                    ? (env.eventId as string)
                    : undefined,
                timestamp:
                  typeof env.timestamp === 'string'
                    ? (env.timestamp as string)
                    : undefined,
                actorSub:
                  typeof env.actorSub === 'string'
                    ? (env.actorSub as string)
                    : undefined,
                payload,
                reason,
              });
            } catch (e) {
              // Don't let a caller-side exception tear down the
              // subscription; log so dev sees it but keep the stream
              // alive for sibling consumers.
              console.warn('[useUserChannel] onNotification threw:', e);
            }
          }
        },
        onStatus: (status) => {
          if (!active) return;
          setConnected(status === 'connected');
        },
        onError: (err) => {
          if (!active) return;
          setError(
            typeof err === 'string' ? err : JSON.stringify(err ?? {}),
          );
        },
      },
    );

    return () => {
      active = false;
      unsubscribe();
    };
  }, [userSub, httpDns, realtimeDns]);

  return { connected, error };
}
